<template>
  <div class="h-full w-full overflow-hidden xl:overflow-visible">
    <slot v-if="!props.hideHeader" name="header">
      <Container class="!px-0 sm:!px-4 md:w-[80%] md:!px-9 lg:w-full">
        <ProgressBar
          :current-step="activeStepIndex"
          :total-steps="stepsArray.length"
          :theme="theme"
        />
        <NavigationBar
          :next-step="getStep(activeStepIndex + 1)"
          :prev-step="
            activeStepIndex === 0 && props.firstStepBack
              ? props.firstStepBack
              : getStep(activeStepIndex - 1)
          "
          :theme="theme"
          @next="nextStep"
          @prev="prevStep"
        />
      </Container>
    </slot>
    <div class="relative">
      <div v-for="(step, index) in stepsArray" :id="step + index" :key="index">
        <transition :name="transitionName">
          <div
            v-if="props.renderMode === 'eager' || visitedStepIndexes.has(index)"
            v-show="activeStepIndex === index"
            class="relative w-full"
          >
            <div class="mx-auto md:!px-9 lg:w-full">
              <slot :name="stepsArray[index]" :next-step="nextStep" :prev-step="prevStep" />
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import Container from '../atoms/Container.vue'
import NavigationBar from './NavigationBar.vue'
import ProgressBar from './ProgressBar.vue'
import type { ISteps } from '../../types'
import { THEMES } from '../../types/themes'

const emit = defineEmits<{ 'prev-first': [] }>()

const props = withDefaults(
  defineProps<{
    steps: ISteps
    initStep: number
    theme?: THEMES
    hideHeader?: boolean
    scrollTop?: boolean
    firstStepBack?: { title: string }
    renderMode?: 'eager' | 'visited'
  }>(),
  {
    theme: THEMES.DEFAULT,
    hideHeader: false,
    scrollTop: true,
    renderMode: 'eager',
  },
)

const TRANSITIONS = {
  NEXT: 'step-next',
  PREV: 'step-prev',
}

const activeStepIndex = ref<number>(props.initStep || 0)
const transitionName = ref('step-next')
const visitedStepIndexes = ref(new Set<number>([activeStepIndex.value]))

const stepsArray = computed(() => {
  return Object.entries(props.steps)
    .filter(([, value]) => value.visible === true)
    .map(el => el[0])
})

const getStep = (index: number) => {
  const key = stepsArray.value[index]
  if (!key) return undefined
  const step = props.steps[key]
  return step
}

const nextStep = () => {
  transitionName.value = TRANSITIONS.NEXT
  if (activeStepIndex.value === stepsArray.value.length - 1) {
    return
  }
  activeStepIndex.value++
  scrollToTop()
}

const prevStep = () => {
  transitionName.value = TRANSITIONS.PREV
  if (activeStepIndex.value === 0) {
    if (props.firstStepBack) emit('prev-first')
    return
  }
  activeStepIndex.value--
  scrollToTop()
}

let timeout: NodeJS.Timeout

const scrollToTop = () => {
  if (props.scrollTop) {
    timeout = setTimeout(() => window.scrollTo(0, 0), 800)
  }
}

const stopWatcher = watch(
  () => props.initStep,
  () => {
    activeStepIndex.value = props.initStep
    visitedStepIndexes.value.add(props.initStep)
  },
)

const stopVisitedWatcher = watch(activeStepIndex, value => {
  visitedStepIndexes.value.add(value)
})

onBeforeUnmount(() => {
  stopWatcher()
  stopVisitedWatcher()
  clearTimeout(timeout)
})
</script>

<style scoped>
.step-next-enter-active,
.step-next-leave-active,
.step-prev-enter-active,
.step-prev-leave-active {
  transition: transform 0.8s ease-in-out;
}
.step-next-enter-from {
  transform: translateX(100%);
}

.step-next-enter-active {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
}

.step-next-leave-to {
  transform: translateX(-100%);
}

.step-prev-enter-from {
  transform: translateX(-100%);
}

.step-prev-enter-active {
  position: absolute;
  top: 0;
  width: 100%;
  height: 100%;
}

.step-prev-leave-to {
  transform: translateX(100%);
}
</style>
